import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { Download, Inbox, ListFilter, MoreVertical } from "lucide-react";

import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { IconAction } from "@/components/shared/IconAction";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDate, formatTZS } from "@/utils/formatters";
import { useStudentPayments } from "@/hooks/useStudentData";

export const Route = createFileRoute("/student/payments/")({
  component: StudentPayments,
});

function StudentPayments() {
  const { data: paymentsData = [], isLoading } = useStudentPayments();
  const [search, setSearch] = useState("");

  if (isLoading) {
    return <TableSkeleton rows={5} columns={4} />;
  }

  return (
    <div>
      <PageHeader
        title="Payment history"
        subtitle="Detailed ledger of your tuition and institutional transactions."
        action={
          <Button variant="outline">
            <Download />
            Export ledger
          </Button>
        }
      />

      <SectionCard
        title="Transactions"
        noPadding
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search records..."
              aria-label="Search records"
            />
            <FilterDropdown label="Category" options={["All", "Tuition", "Library", "Exam"]} />
            <FilterDropdown label="Status" options={["All", "Completed", "Pending", "Canceled"]} />
            <Button variant="ghost" size="sm">
              <ListFilter />
              More filters
            </Button>
          </div>
        }
      >
        {paymentsData.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Transaction</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paymentsData.map((p: any) => (
                <TableRow key={p.id}>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatDate(p.date)}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-foreground">{p.description}</p>
                    <p className="font-mono text-caption text-muted-foreground">
                      ID: #{p.id.toString().slice(-6).toUpperCase()}
                    </p>
                  </TableCell>
                  <TableCell className="text-body">{p.type || "Other"}</TableCell>
                  <TableCell className="text-right">
                    <p className="font-mono whitespace-nowrap text-foreground tabular-nums">
                      {formatTZS(p.amount)}
                    </p>
                    {p.meta && <p className="text-caption text-muted-foreground">{p.meta}</p>}
                  </TableCell>
                  <TableCell>
                    <StatusIndicator status={p.status} />
                  </TableCell>
                  <TableCell>
                    <IconAction label="More actions" icon={<MoreVertical />} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            bare
            icon={<Inbox />}
            title="No payments found"
            description="Your transaction ledger is currently empty. Any upcoming or processed payments will appear here."
          />
        )}
      </SectionCard>
    </div>
  );
}

// --- Minimalist Sub-components ---

function FilterDropdown({ label, options }: { label: string; options: string[] }) {
  const toValue = (opt: string) => (opt === "All" ? "all" : opt);
  return (
    <Select defaultValue={toValue(options[0])}>
      <SelectTrigger className="h-8 w-[130px]" aria-label={label}>
        <span className="text-muted-foreground">{label}:</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt} value={toValue(opt)}>
            {opt}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function StatusIndicator({ status }: { status: string }) {
  const map: any = {
    completed: { variant: "success", label: "Completed" },
    pending: { variant: "warning", label: "Pending" },
    canceled: { variant: "destructive", label: "Canceled" },
  };

  const s = map[status] || map.completed;

  return (
    <Badge variant={s.variant} dot>
      {s.label}
    </Badge>
  );
}

export default StudentPayments;
